if (!customElements.get('siha-benefits')) {
  class SihaBenefits extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.scrollFrame = null;
      this.resumeTimer = null;
      this.isPointerDown = false;
    }

    connectedCallback() {
      if (this.isConnectedOnce) return;

      this.track = this.querySelector('[data-benefits-track]');
      this.cards = Array.from(this.querySelectorAll('[data-benefit-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-benefit-dot]'));
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Math.max(Number.parseInt(this.dataset.speed, 10) || 4000, 3000);

      if (!this.track || this.cards.length < 1) return;

      this.onScroll = this.handleScroll.bind(this);
      this.onDotClick = this.handleDotClick.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onFocusIn = this.stopAutoplay.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onMediaChange = this.handleMediaChange.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onMediaChange);
        this.reduceMotionQuery.addEventListener('change', this.onMediaChange);
      } else {
        this.mobileQuery.addListener(this.onMediaChange);
        this.reduceMotionQuery.addListener(this.onMediaChange);
      }

      this.resizeObserver = new ResizeObserver(() => {
        if (this.mobileQuery.matches) this.goTo(this.currentIndex, false);
      });
      this.resizeObserver.observe(this.track);

      this.isConnectedOnce = true;
      this.setActive(0);
      this.handleMediaChange();
    }

    disconnectedCallback() {
      if (!this.isConnectedOnce) return;

      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
      window.cancelAnimationFrame(this.scrollFrame);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.removeEventListener('change', this.onMediaChange);
        this.reduceMotionQuery.removeEventListener('change', this.onMediaChange);
      } else {
        this.mobileQuery.removeListener(this.onMediaChange);
        this.reduceMotionQuery.removeListener(this.onMediaChange);
      }

      this.resizeObserver?.disconnect();
      this.isConnectedOnce = false;
    }

    canAutoplay() {
      return (
        this.autoplay &&
        this.cards.length > 1 &&
        this.mobileQuery.matches &&
        !this.reduceMotionQuery.matches &&
        document.visibilityState === 'visible'
      );
    }

    startAutoplay(delay = this.speed) {
      this.stopAutoplay();
      if (!this.canAutoplay() || this.isPointerDown) return;

      this.timer = window.setTimeout(() => {
        this.goTo((this.currentIndex + 1) % this.cards.length, true);
        this.startAutoplay();
      }, delay);
    }

    stopAutoplay() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    restartAutoplay(delay = this.speed) {
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.startAutoplay(), delay);
    }

    setActive(index) {
      this.currentIndex = Math.max(0, Math.min(index, this.cards.length - 1));

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === this.currentIndex);
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
    }

    goTo(index, smooth = true) {
      if (!this.mobileQuery.matches) return;

      const normalizedIndex = (index + this.cards.length) % this.cards.length;
      const card = this.cards[normalizedIndex];
      const left = card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;

      this.setActive(normalizedIndex);
      this.track.scrollTo({
        left,
        behavior: smooth && !this.reduceMotionQuery.matches ? 'smooth' : 'auto',
      });
    }

    handleScroll() {
      if (!this.mobileQuery.matches || this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(trackCenter - cardCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.setActive(closestIndex);
        this.scrollFrame = null;
      });
    }

    handleDotClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.benefitDot, 10);
      this.goTo(index, true);
      this.restartAutoplay();
    }

    handlePointerDown() {
      this.isPointerDown = true;
      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
    }

    handlePointerUp() {
      this.isPointerDown = false;
      this.restartAutoplay(1200);
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.restartAutoplay(1200);
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    handleMediaChange() {
      this.stopAutoplay();

      if (this.mobileQuery.matches) {
        window.requestAnimationFrame(() => this.goTo(this.currentIndex, false));
        this.startAutoplay();
      } else {
        this.track.scrollTo({ left: 0, behavior: 'auto' });
      }
    }
  }

  customElements.define('siha-benefits', SihaBenefits);
}

